import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { Box } from "Components/Base/Layout/Box";
import { Grid } from "Components/Base/Layout/Grid";
import { Accordion } from "Components/Base/Surfaces/Accordion";
import { useDisplayLearningActivityCardImage } from "lib/hocs/useLearningActivityCardState";
import Card from "./Card";
import LangContext from "../../../../langContext";
import { groupProgramSteps } from "../../../../functions";
import { StyledGroupSummary, StyledProgramGroup } from "../../style";
import {
  PROP_TYPE_PROGRAM_CONTENT,
  PROP_DEFAULT_PROGRAM_CONTENT,
} from "../../../../propTypes";

const StepCardGrid = ({
  steps,
  startIndex,
  activityId,
  onRefresh,
  programParticipant,
  isPreview,
  isManager,
  displayCardImage,
}) => (
  <Grid container spacing={2} className="program-cards">
    {steps.map((course, index) => {
      const courseWithIndex = { ...course, index: startIndex + index };
      return (
        <Card
          key={`${course.type}-${course.courseId}-${course.activityId}-${startIndex + index}`}
          course={courseWithIndex}
          order={startIndex + index}
          displayImage={displayCardImage}
          activityId={activityId}
          onRefresh={onRefresh}
          programParticipant={programParticipant}
          isPreview={isPreview}
          isManager={isManager}
        />
      );
    })}
  </Grid>
);

StepCardGrid.propTypes = {
  steps: PROP_TYPE_PROGRAM_CONTENT,
  startIndex: PropTypes.number,
  activityId: PropTypes.number,
  onRefresh: PropTypes.func,
  programParticipant: PropTypes.string,
  isPreview: PropTypes.bool,
  isManager: PropTypes.bool,
  displayCardImage: PropTypes.bool,
};

const ProgramCards = (props) => {
  const {
    steps,
    allSteps,
    activityId,
    onRefresh,
    programParticipant,
    isPreview,
    isManager,
  } = props;
  const languageContext = useContext(LangContext);
  const displayCardImage = useDisplayLearningActivityCardImage();
  const [collapsedGroups, setCollapsedGroups] = useState([]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId],
    );
  };

  const blocks = groupProgramSteps(steps);
  const groupTotals = new Map(
    groupProgramSteps(allSteps).map((block) => [
      block.groupId,
      {
        total: block.steps.length,
        completed: block.steps.filter((step) => step.completed).length,
      },
    ]),
  );
  let stepIndex = 0;

  return (
    <Box sx={{ padding: "32px 0 !important", marginLeft: "16px" }}>
      {blocks.map((block) => {
        const startIndex = stepIndex;
        stepIndex += block.steps.length;

        if (block.groupId === 0) {
          return (
            <StepCardGrid
              key={block.key}
              steps={block.steps}
              startIndex={startIndex}
              activityId={activityId}
              onRefresh={onRefresh}
              programParticipant={programParticipant}
              isPreview={isPreview}
              isManager={isManager}
              displayCardImage={displayCardImage}
            />
          );
        }

        const totals = groupTotals.get(block.groupId) ?? {
          total: block.steps.length,
          completed: block.steps.filter((step) => step.completed).length,
        };

        return (
          <StyledProgramGroup key={block.key}>
            <Accordion
              border
              expanded={!collapsedGroups.includes(block.groupId)}
              onChange={() => toggleGroup(block.groupId)}
              summaryText={
                <StyledGroupSummary>
                  <span>{block.title}</span>
                  <span className="program-group-progress">
                    {`${totals.total} ${languageContext(totals.total === 1 ? "step" : "steps")} · ${totals.completed}/${totals.total} ${languageContext("completed")}`}
                  </span>
                </StyledGroupSummary>
              }
              detailsText={
                <StepCardGrid
                  steps={block.steps}
                  startIndex={startIndex}
                  activityId={activityId}
                  onRefresh={onRefresh}
                  programParticipant={programParticipant}
                  isPreview={isPreview}
                  isManager={isManager}
                  displayCardImage={displayCardImage}
                />
              }
            />
          </StyledProgramGroup>
        );
      })}
    </Box>
  );
};

ProgramCards.propTypes = {
  steps: PROP_TYPE_PROGRAM_CONTENT,
  allSteps: PROP_TYPE_PROGRAM_CONTENT,
};

ProgramCards.defaultProps = {
  steps: PROP_DEFAULT_PROGRAM_CONTENT,
  allSteps: PROP_DEFAULT_PROGRAM_CONTENT,
};

export default ProgramCards;
